/**
 * Le logo MeTube — ⟦A-144 · C19⟧.
 *
 * Remplace l'ancien disque violet + lettre « M » en texte (⟦A-138⟧) par un vrai dessin pixel :
 * une petite télé cathodique 8-bit (MeTube = *le tube*) dont l'écran porte le monogramme ΣƎ —
 * un M couché à 90° (pointes à gauche) dont les deux pieds filent à droite et se soudent aux
 * barres d'un E inversé, sans espace entre les lettres (« ça ferme la forme »). Variante V1
 * retenue par Mathieu le 10/09 : *« V1 avec la télé, vas-y lance C19 »*.
 *
 * Grilles EXACTES : `docs/chantiers/C19-le-logo-tele.md`. Les chemins ci-dessous sont générés à
 * partir de ces grilles puis vérifiés PIXEL À PIXEL (342/342, zéro écart) contre
 * `assets/making-of/2026-09-10-logo/logo.py`, la référence exécutable citée par le chantier —
 * en cas de doute sur une case, c'est ce script qui a raison, pas ce fichier.
 *
 * SVG inline, un `<path>` par couleur (pas un élément par pixel), `shapeRendering="crispEdges"`
 * pour rester net à toute taille multiple entière du pixel logique (19×18). Couleurs = tokens
 * (`tokens.css`), jamais en dur, pour suivre le thème comme le reste de l'UI :
 *   - corps + monogramme → `--principal` (le violet d'accent du jeu)
 *   - ombre du corps     → `--principal-ombre`
 *   - écran              → `--papier` (la carte crème, partout où un dialogue s'ouvre)
 *   - antennes / pieds   → `--marqueur` (le trait sombre)
 *   - boutons de la télé → `--or` — AUCUN bouton « Paramètres » n'est jaune dans le code actuel
 *     (`.bouton.discret` du menu est crème) ; `--or` est le seul jaune déjà posé sur un bouton
 *     du jeu (`.bouton-or`, Boutique). Choix documenté ici plutôt qu'une couleur inventée en
 *     dur — voir l'avenant A-144 pour le détail de cet écart avec le chantier.
 *
 * `variante="tele"` (par défaut) : la télé complète, posée partout selon la décision de
 * Mathieu. `variante="monogramme"` : le monogramme seul (11×7), crème sur violet — même
 * vocabulaire que l'ancien disque — pour les endroits où 19×18 ne rentre pas (favicon, onglet).
 *
 * Toujours décoratif (`aria-hidden`) : le texte à côté (« MeTube Studio », etc.) porte déjà le
 * nom accessible.
 *
 * `data-logo-metube` ⟦A-176 · C59⟧ : l'attache de la voie tactile du mode triche (sept tapes
 * rapides sur la télé, `PanneauDev`). Un attribut, aucun écouteur ici : le logo reste décoratif.
 */

import type { SVGProps } from "react";

type VarianteLogo = "tele" | "monogramme";

interface LogoMeTubeProps extends Omit<SVGProps<SVGSVGElement>, "viewBox" | "children" | "aria-hidden"> {
  variante?: VarianteLogo;
}

/** Corps de la télé (V) fusionné avec le monogramme (même violet, même path) — grille TELE 19×18
 *  + MONO_V1 11×7 posé aux colonnes 3-13 / lignes 7-13, encodés en runs horizontaux par ligne. */
const D_CORPS =
  "M0 5h19v1h-19zM0 6h1v1h-1zM16 6h1v1h-1zM18 6h1v1h-1zM0 7h1v1h-1zM3 7h11v1h-11zM16 7h1v1h-1zM18 7h1v1h-1zM0 8h1v1h-1zM4 8h1v1h-1zM13 8h1v1h-1zM16 8h3v1h-3zM0 9h1v1h-1zM5 9h1v1h-1zM13 9h1v1h-1zM16 9h1v1h-1zM18 9h1v1h-1zM0 10h1v1h-1zM6 10h8v1h-8zM16 10h3v1h-3zM0 11h1v1h-1zM5 11h1v1h-1zM13 11h1v1h-1zM16 11h1v1h-1zM18 11h1v1h-1zM0 12h1v1h-1zM4 12h1v1h-1zM13 12h1v1h-1zM16 12h3v1h-3zM0 13h1v1h-1zM3 13h11v1h-11zM16 13h1v1h-1zM18 13h1v1h-1zM0 14h1v1h-1zM16 14h3v1h-3zM0 15h1v1h-1zM16 15h3v1h-3zM0 16h19v1h-19z";

/** Ombre du corps (O). */
const D_OMBRE =
  "M1 6h15v1h-15zM1 7h1v1h-1zM15 7h1v1h-1zM1 8h1v1h-1zM15 8h1v1h-1zM1 9h1v1h-1zM15 9h1v1h-1zM1 10h1v1h-1zM15 10h1v1h-1zM1 11h1v1h-1zM15 11h1v1h-1zM1 12h1v1h-1zM15 12h1v1h-1zM1 13h1v1h-1zM15 13h1v1h-1zM1 14h1v1h-1zM15 14h1v1h-1zM1 15h15v1h-15z";

/** Écran (E) — les cases recouvertes par le monogramme en sont exclues (elles sont dans D_CORPS). */
const D_ECRAN =
  "M2 7h1v1h-1zM14 7h1v1h-1zM2 8h2v1h-2zM5 8h8v1h-8zM14 8h1v1h-1zM2 9h3v1h-3zM6 9h7v1h-7zM14 9h1v1h-1zM2 10h4v1h-4zM14 10h1v1h-1zM2 11h3v1h-3zM6 11h7v1h-7zM14 11h1v1h-1zM2 12h2v1h-2zM5 12h8v1h-8zM14 12h1v1h-1zM2 13h1v1h-1zM14 13h1v1h-1zM2 14h13v1h-13z";

/** Antennes en V + les deux pieds (A). */
const D_ANTENNES =
  "M2 0h1v1h-1zM12 0h1v1h-1zM3 1h1v1h-1zM11 1h1v1h-1zM4 2h1v1h-1zM10 2h1v1h-1zM5 3h1v1h-1zM9 3h1v1h-1zM6 4h3v1h-3zM2 17h2v1h-2zM13 17h2v1h-2z";

/** Les quatre boutons (J). */
const D_BOUTONS = "M17 6h1v1h-1zM17 7h1v1h-1zM17 9h1v1h-1zM17 11h1v1h-1zM17 13h1v1h-1z";

/** Monogramme SEUL (11×7), pour `variante="monogramme"`. */
const D_MONOGRAMME_SEUL =
  "M0 0h11v1h-11zM1 1h1v1h-1zM10 1h1v1h-1zM2 2h1v1h-1zM10 2h1v1h-1zM3 3h8v1h-8zM2 4h1v1h-1zM10 4h1v1h-1zM1 5h1v1h-1zM10 5h1v1h-1zM0 6h11v1h-11z";

export function LogoMeTube({ variante = "tele", ...props }: LogoMeTubeProps) {
  if (variante === "monogramme") {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        {...props}
        data-logo-metube="true"
        viewBox="0 0 11 7"
        shapeRendering="crispEdges"
        aria-hidden="true"
      >
        <rect x={0} y={0} width={11} height={7} fill="var(--principal)" />
        <path d={D_MONOGRAMME_SEUL} fill="var(--papier)" />
      </svg>
    );
  }

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      {...props}
      data-logo-metube="true"
      viewBox="0 0 19 18"
      shapeRendering="crispEdges"
      aria-hidden="true"
    >
      <path d={D_ECRAN} fill="var(--papier)" />
      <path d={D_OMBRE} fill="var(--principal-ombre)" />
      <path d={D_CORPS} fill="var(--principal)" />
      <path d={D_ANTENNES} fill="var(--marqueur)" />
      <path d={D_BOUTONS} fill="var(--or)" />
    </svg>
  );
}
